import {
  DeleteOutlined,
  EditOutlined,
  ExpandAltOutlined,
} from '@ant-design/icons';
import { QueryObserverResult } from '@tanstack/react-query';
import { App, Button, Flex, Popconfirm } from 'antd';
import { useTranslation } from 'translations/hooks';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { SegmentHistory } from 'business/quality-control/pages/segment-history/hooks/data/types';
import {
  SegmentHistoryV1ControllerGetSegmentsHistoryError,
  useSegmentV1ControllerDeleteSegment,
} from 'generated/apiComponents';
import { SegmentHistoryEntriesV1OutputDto } from 'generated/apiSchemas';
import {
  Module_Enum,
  Quality_SegmentHistoryStatusEnum_Enum,
} from 'generated/graphql';

export const ActionsColumn = ({
  record,
  onOpenDrawer,
  onOpenEditModal,
  mobile = false,
  isDataManager = false,
  isConstructionSiteManager = false,
  refetchData,
}: {
  record: SegmentHistory;
  onOpenDrawer: (segment: SegmentHistory) => void;
  onOpenEditModal?: (segment: SegmentHistory) => void;
  mobile?: boolean;
  isDataManager?: boolean;
  isConstructionSiteManager?: boolean;
  refetchData?: () => Promise<
    QueryObserverResult<
      SegmentHistoryEntriesV1OutputDto,
      SegmentHistoryV1ControllerGetSegmentsHistoryError
    >
  >;
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const restHeaders = useRestApiHeaders(Module_Enum.QualityControl);
  const { mutate } = useSegmentV1ControllerDeleteSegment({
    onSuccess: async () => {
      await refetchData?.();
      message.open({
        type: 'success',
        content: t(
          'qualityControl.segmentHistory.expandedColumns.delete.success',
        ),
      });
    },
    onError: () => {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.segmentHistory.expandedColumns.delete.error',
        ),
      });
    },
  });

  if (mobile) {
    return (
      <Button
        type="text"
        icon={<ExpandAltOutlined />}
        onClick={() => onOpenDrawer(record)}
      />
    );
  }

  const shouldDisplayDetails = record.history.some(
    (history) =>
      history.status === Quality_SegmentHistoryStatusEnum_Enum.Quarantine ||
      (history.status === Quality_SegmentHistoryStatusEnum_Enum.Rejected &&
        history.segmentRefusal != null),
  );

  return (
    <Flex gap="small">
      {isDataManager || isConstructionSiteManager ? (
        <Button
          color="default"
          variant="filled"
          icon={<EditOutlined />}
          onClick={() => onOpenEditModal?.(record)}
        />
      ) : null}
      {shouldDisplayDetails ? (
        <Button
          color="default"
          variant="filled"
          onClick={() => {
            onOpenDrawer(record);
          }}
        >
          {t('qualityControl.segmentHistory.actions.openDetails')}
        </Button>
      ) : null}
      {isDataManager ? (
        <Popconfirm
          title={t('qualityControl.segmentHistory.actions.delete')}
          placement="topRight"
          onConfirm={() => {
            mutate({ pathParams: { id: record.id }, headers: restHeaders });
          }}
        >
          <Button color="default" variant="filled" icon={<DeleteOutlined />} />
        </Popconfirm>
      ) : null}
    </Flex>
  );
};
